import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { LoadingBlock } from '../components/LoadingBlock';
import { SectionCard } from '../components/SectionCard';

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  birth_date: '',
  guardian_name: '',
  guardian_contact: '',
  guardian_document: '',
  guardian_consent: false,
  compliance_notes: '',
  password: '',
  role_code: 'fan',
  status: 'active',
};

const ROLE_OPTIONS = [
  { value: 'organizer', label: 'Organizador' },
  { value: 'team_owner', label: 'Responsável pelo time' },
  { value: 'store', label: 'Loja' },
  { value: 'referee', label: 'Juiz' },
  { value: 'player', label: 'Atleta/Jogador' },
  { value: 'fan', label: 'Usuário' },
];

const ROLE_LABELS = {
  super_admin: 'Dono do App',
  admin: 'Administrador',
  organizer: 'Organizador',
  team_owner: 'Responsável pelo time',
  store: 'Loja',
  referee: 'Juiz',
  player: 'Atleta/Jogador',
  fan: 'Usuário',
};

const ADULT_ONLY_ROLES = new Set(['super_admin', 'admin', 'organizer', 'referee', 'team_owner', 'store']);

function roleLabel(role) {
  return ROLE_LABELS[role] || role;
}

function ageFromBirthDate(value) {
  if (!value) return null;
  const birth = new Date(`${value}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDelta = today.getMonth() - birth.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
}

function ageGateLabel(row) {
  switch (row.age_gate_status) {
    case 'adult':
      return '18+';
    case 'minor_authorized':
      return '16-17 autorizado';
    case 'minor_blocked':
      return 'Menor bloqueado';
    default:
      return 'Dados pendentes';
  }
}

export function UsersPage({
  loading,
  error,
  data,
  onCreateUser,
  onUpdateUser,
  onDeleteUser,
  creating,
  updating,
  deleting,
  canManage,
  manageableRoles,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const allowedRoles = ROLE_OPTIONS.filter((option) => manageableRoles.includes(option.value));
  const isEditing = Boolean(editingId);
  const age = ageFromBirthDate(form.birth_date);
  const needsGuardian = age !== null && age >= 16 && age < 18;
  const underMinimumAge = age !== null && age < 16;
  const selectedRoleRequiresAdult = ADULT_ONLY_ROLES.has(form.role_code);

  const resetForm = () => {
    setForm({ ...EMPTY_FORM, role_code: allowedRoles[0]?.value || 'fan' });
    setEditingId(null);
  };

  const rows = data?.items || [];
  const auditRows = data?.audit || [];
  const access = data?.access || {};

  if (loading) return <LoadingBlock label="Carregando usuários..." />;
  if (error) return <div className="page-error">{error}</div>;

  return (
    <div className="page-grid two-columns">
      <SectionCard
        title="Usuários"
        subtitle="Crie contas e defina quem pode ser organizador, responsável pelo time, loja, juiz, atleta ou usuário."
        actions={canManage ? (
          <div className="inline-actions">
            <button className="ghost-button" type="button" onClick={resetForm}>Nova conta</button>
            {isEditing ? <button className="ghost-button" type="button" onClick={resetForm}>Cancelar edição</button> : null}
          </div>
        ) : null}
      >
        {canManage ? (
          <form
            className="admin-form"
            onSubmit={(event) => {
              event.preventDefault();
              const payload = { ...form };
              if (!payload.password) {
                delete payload.password;
              }
              if (!payload.role_code) {
                delete payload.role_code;
              }

              if (age !== null && age < 18 && selectedRoleRequiresAdult) {
                window.alert('Perfis administrativos, arbitragem, time ou loja exigem 18 anos.');
                return;
              }

              if (isEditing) {
                onUpdateUser(editingId, payload).then?.(resetForm);
                return;
              }

              onCreateUser(payload).then?.(resetForm);
            }}
          >
            <div className="callout-box compact-box">
              <span className="eyebrow">Acesso do administrador</span>
              <strong>Controle total das contas</strong>
              <p>Juiz, atleta, loja e torcedor entram como usuários. O perfil define o que cada conta pode visualizar ou gerenciar.</p>
              <p>Como dono ou admin, você cria, edita e remove usuários da plataforma, além de definir perfil, status e senha.</p>
            </div>
            <div className="form-row">
              <label>
                Nome
                <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
              </label>
              <label>
                Email
                <input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} required />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Telefone
                <input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
              </label>
              <label>
                Nascimento
                <input type="date" value={form.birth_date} onChange={(event) => setForm({ ...form, birth_date: event.target.value })} required />
              </label>
              <label>
                Perfil
                <select value={form.role_code} onChange={(event) => setForm({ ...form, role_code: event.target.value })}>
                  {form.role_code === '' ? <option value="">Perfil protegido</option> : null}
                  {allowedRoles.map((option) => (
                    <option key={option.value} value={option.value} disabled={age !== null && age < 18 && ADULT_ONLY_ROLES.has(option.value)}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="form-row">
              <label>
                Status
                <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
                  <option value="active">Ativo</option>
                  <option value="pending">Pendente</option>
                  <option value="blocked">Bloqueado</option>
                </select>
              </label>
              <label>
                Observação de conformidade
                <input value={form.compliance_notes} onChange={(event) => setForm({ ...form, compliance_notes: event.target.value })} />
              </label>
            </div>
            {underMinimumAge ? (
              <div className="page-error small">Contas no app são permitidas somente a partir de 16 anos.</div>
            ) : null}
            {age !== null && age < 18 && selectedRoleRequiresAdult ? (
              <div className="page-error small">Perfis administrativos, arbitragem, time ou loja exigem 18 anos.</div>
            ) : null}
            {needsGuardian ? (
              <>
                <div className="form-row triple">
                  <label>
                    Responsável legal
                    <input value={form.guardian_name} onChange={(event) => setForm({ ...form, guardian_name: event.target.value })} required />
                  </label>
                  <label>
                    Contato do responsável
                    <input value={form.guardian_contact} onChange={(event) => setForm({ ...form, guardian_contact: event.target.value })} required />
                  </label>
                  <label>
                    Documento do responsável
                    <input value={form.guardian_document} onChange={(event) => setForm({ ...form, guardian_document: event.target.value })} />
                  </label>
                </div>
                <label className="checkbox-field">
                  <span>Autorização do responsável registrada</span>
                  <input type="checkbox" checked={Boolean(form.guardian_consent)} onChange={(event) => setForm({ ...form, guardian_consent: event.target.checked })} required />
                </label>
              </>
            ) : null}
            <label>
              {isEditing ? 'Nova senha opcional' : 'Senha'}
              <input
                type="password"
                value={form.password}
                onChange={(event) => setForm({ ...form, password: event.target.value })}
                required={!isEditing}
              />
            </label>
            <button className="primary-button" type="submit" disabled={creating || updating}>
              {isEditing ? (updating ? 'Salvando...' : 'Atualizar usuário') : (creating ? 'Criando...' : 'Criar usuário')}
            </button>
          </form>
        ) : (
          <div className="empty-state">Seu perfil não pode criar ou editar contas.</div>
        )}
      </SectionCard>

      <SectionCard title="Lista de usuários" subtitle="Os perfis governam o que cada conta pode alterar no sistema.">
        <DataTable
          rows={rows}
          columns={[
            { key: 'name', label: 'Nome' },
            { key: 'email', label: 'Email' },
            { key: 'phone', label: 'Telefone' },
            {
              key: 'roles',
              label: 'Perfil',
              render: (row) => (row.roles || []).map(roleLabel).join(', ') || '-',
            },
            {
              key: 'age_gate_status',
              label: 'Verificação de idade',
              render: (row) => ageGateLabel(row),
            },
            {
              key: 'social_features_enabled',
              label: 'Social',
              render: (row) => row.social_features_enabled ? 'Liberado' : 'Bloqueado',
            },
            { key: 'status', label: 'Status', type: 'status' },
            {
              key: 'actions',
              label: 'Ações',
              render: (row) => (
                <div className="inline-actions">
                  {canManage ? (
                    <>
                      <button
                        className="inline-button"
                        type="button"
                        onClick={() => {
                          setEditingId(row.id);
                          const editableRole = (row.roles || []).find((role) => allowedRoles.some((option) => option.value === role));
                          setForm({
                            name: row.name || '',
                            email: row.email || '',
                            phone: row.phone || '',
                            birth_date: row.birth_date || '',
                            guardian_name: row.guardian_name || '',
                            guardian_contact: row.guardian_contact || '',
                            guardian_document: row.guardian_document || '',
                            guardian_consent: Boolean(row.guardian_consent_at),
                            compliance_notes: row.compliance_notes || '',
                            password: '',
                            role_code: editableRole || '',
                            status: row.status || 'active',
                          });
                        }}
                      >
                        Editar
                      </button>
                      <button className="inline-button danger-text" type="button" onClick={() => onDeleteUser(row.id)} disabled={deleting}>
                        Remover
                      </button>
                    </>
                  ) : (
                    <span className="table-muted">Somente leitura</span>
                  )}
                </div>
              ),
            },
          ]}
        />
      </SectionCard>

      <SectionCard title="Permissões efetivas" subtitle="Bloqueio visual por módulo e ação disponível para o perfil logado.">
        <div className="timeline-list">
          {Object.entries(access).map(([module, abilities]) => (
            <article key={module} className="timeline-item">
              <div>
                <strong>{module}</strong>
                <p>Visualizar: {abilities.view ? 'sim' : 'não'} • Gerenciar: {abilities.manage ? 'sim' : 'não'}</p>
              </div>
            </article>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Auditoria recente" subtitle="Quem alterou cada item e qual módulo foi afetado.">
        <div className="timeline-list">
          {auditRows.length ? auditRows.map((item) => (
            <article key={item.id} className="timeline-item">
              <div>
                <strong>{item.module} • {item.action_name}</strong>
                <p>{item.user_name || 'Sistema'} • {item.entity_type || '-'} #{item.entity_id || '-'}</p>
                <span>{item.created_at}</span>
              </div>
            </article>
          )) : <div className="empty-state">Nenhum log de auditoria recente.</div>}
        </div>
      </SectionCard>
    </div>
  );
}
